const pad = (value) => String(value).padStart(2, "0");
/** The provider convention for a timeshift instant: `YYYY-MM-DD:HH-MM`, in UTC. */
function stamp(ms) {
    const d = new Date(ms);
    return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}:${pad(d.getUTCHours())}-${pad(d.getUTCMinutes())}`;
}
/** True when this channel advertises replay AND we hold the template to build one. */
export function hasCatchup(channel) {
    return channel.catchupAvailable === true && typeof channel.catchupSource === "string" && channel.catchupSource.length > 0;
}
/** The provider convention for a timeshift instant as a PATH stamp: `YYYY-MM-DD:HH-MM`, UTC. */
export function timeshiftStamp(ms) {
    return stamp(ms);
}
/**
 * The Xtream timeshift URL for a past programme, built from the channel's own live URL
 * (`…/live/{user}/{pass}/{id}.ts`, or the redacted form — the credentials sit in the same
 * two segments either way, so a redacted live URL gives a redacted replay URL that
 * hydrateRedactedUrl fills at playback).
 *
 * Returns `undefined` when the live URL is not that shape, so nothing is guessed.
 */
export function xtreamTimeshiftUrl(streamUrl, window, format = "ts") {
    const parts = xtreamLiveUrlParts(streamUrl);
    if (!parts)
        return undefined;
    const minutes = Math.max(1, Math.round((window.endMs - window.startMs) / 60_000));
    return `${parts.origin}/timeshift/${parts.username}/${parts.password}/${minutes}/${stamp(window.startMs)}/${parts.id}.${format}`;
}
/** Split `…/live/{user}/{pass}/{id}[.ext]` (or the same without `live`) into its parts. */
function xtreamLiveUrlParts(streamUrl) {
    let url;
    try {
        url = new URL(streamUrl);
    }
    catch {
        return undefined;
    }
    const segments = url.pathname.split("/").filter(Boolean);
    const tail = segments[0] === "live" ? segments.slice(1) : segments;
    if (tail.length !== 3)
        return undefined;
    const [username, password, last] = tail;
    const id = (last ?? "").replace(/\.[A-Za-z0-9]+$/, "");
    if (!username || !password || !/^[0-9]+$/.test(id))
        return undefined;
    return { origin: url.origin, username, password, id };
}
/**
 * The provider's own numeric stream id, read back out of a channel's live URL.
 *
 * Catch-up needs it to ask the panel for ONE channel's archive listing
 * (`get_simple_data_table`). `Channel.epgChannelId` is not reliable for this —
 * it carries an XMLTV `<channel id>` for M3U sources — whereas the live URL is
 * the id the panel itself issued. Undefined when the URL is not Xtream-shaped.
 */
export function xtreamStreamId(streamUrl) {
    return xtreamLiveUrlParts(streamUrl)?.id;
}
/**
 * Expand every placeholder in a declared template.
 *
 * Both spellings seen in the wild are supported — `{utc}` and `${utc}` — plus the
 * strftime-style `%Y%m%d` family. Unknown placeholders make the whole expansion fail
 * (return `undefined`), so a template we do not fully understand never produces a URL.
 */
export function expandCatchupTemplate(template, window) {
    const start = new Date(window.startMs);
    const values = {
        // Full instants
        start: stamp(window.startMs),
        end: stamp(window.endMs),
        // Unix times (seconds). `lutc` is the local-time twin panels emit; we use the same
        // instant, because the epoch is timezone-independent whatever the placeholder is called.
        utc: String(Math.floor(window.startMs / 1000)),
        lutc: String(Math.floor(window.startMs / 1000)),
        utcend: String(Math.floor(window.endMs / 1000)),
        lutcend: String(Math.floor(window.endMs / 1000)),
        timestamp: String(window.startMs),
        duration: String(Math.max(1, Math.round((window.endMs - window.startMs) / 60000))),
        // UTC components, for templates that build the stamp themselves
        Y: String(start.getUTCFullYear()),
        m: pad(start.getUTCMonth() + 1),
        d: pad(start.getUTCDate()),
        H: pad(start.getUTCHours()),
        M: pad(start.getUTCMinutes()),
        S: pad(start.getUTCSeconds()),
    };
    let unresolved;
    const expanded = template.replace(/\$\{(\w+)\}|\{(\w+)\}|%([A-Za-z])/g, (match, braced, bare, strftime) => {
        const name = braced ?? bare;
        if (name !== undefined) {
            const value = values[name];
            if (value === undefined) {
                unresolved ??= match;
                return match;
            }
            return value;
        }
        // strftime: only the letters that map onto a component above.
        const value = values[strftime];
        if (value === undefined) {
            unresolved ??= match;
            return match;
        }
        return value;
    });
    return unresolved === undefined ? expanded : undefined;
}
/**
 * The URL to replay a past programme on this channel, or `undefined` when the provider
 * declared nothing usable.
 *
 * `catchup="append"` templates are query fragments (`?utc={utc}&lutc={lutc}`) meant to be
 * added to the channel's live URL; every other template is the replay URL in full.
 */
export function catchupUrlFor(channel, window, format = "ts") {
    if (channel.catchupAvailable !== true)
        return undefined;
    // No declared template (every Xtream source): the measured timeshift path.
    // `format` applies only here — a declared template's form is the provider's.
    if (!hasCatchup(channel))
        return xtreamTimeshiftUrl(channel.streamUrl, window, format);
    const expanded = expandCatchupTemplate(channel.catchupSource, window);
    if (expanded === undefined)
        return undefined;
    if (channel.catchupType === "append") {
        const separator = channel.streamUrl.includes("?") ? "&" : "?";
        const fragment = expanded.replace(/^[?&]/, "");
        return `${channel.streamUrl}${separator}${fragment}`;
    }
    return expanded;
}
/**
 * Whether a past programme is inside the archive depth the provider advertised.
 * Without a declared depth there is nothing to check against, so the programme is
 * allowed through — the replay itself is the authority, not a number we guessed.
 */
export function withinCatchupWindow(channel, window, nowMs) {
    if (channel.catchupDays === undefined)
        return true;
    const ageMs = nowMs - window.startMs;
    return ageMs <= channel.catchupDays * 24 * 60 * 60 * 1000;
}
//# sourceMappingURL=catchup.js.map